import {THEME} from '@wandb/weave/common/css/globals.styles';
import React from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {type AxisDomainItem} from 'recharts/types/util/types';

import {
  CHART_HEIGHT,
  ChartStatus,
  METRIC_LINE_COLOR,
  TimeRangeSelector,
  type WeaveMetricChartProps,
} from './chartShared';
import {SpanMetricChart} from './SpanMetricChart';
import {
  type ChartDataPoint,
  formatTime,
  useWeaveMetricChartData,
} from './useWeaveMetricChartData';

// Chart color palette, kept in sync with design tokens.
const COLORS = {
  metricLine: METRIC_LINE_COLOR, // primary metric line
  rawValueLine: '#93c5fd', // faded raw-value line when aggregation is shown
  threshold: '#ef4444', // red-500, threshold reference line
  grid: THEME.divider,
  axis: THEME.border,
} as const;

const AGG_LABELS: Record<string, string> = {
  mean: 'Mean',
  median: 'Median',
  min: 'Min',
  max: 'Max',
  mode: 'Mode',
  percentage: '% matching',
  count: 'Count matching',
};

const formatDisplayValue = (value: number | null): string | number => {
  if (value == null) {
    return '—';
  }
  return Number.isInteger(value) ? value : parseFloat(value.toPrecision(4));
};

type MetricTooltipProps = {
  metricPath: string;
  useAggregatedValue: boolean;
  aggLabel: string;
  payload?: Array<{payload: ChartDataPoint}>;
};

const MetricTooltip: React.FC<MetricTooltipProps> = ({
  metricPath,
  useAggregatedValue,
  aggLabel,
  payload,
}) => {
  const point = payload?.[0]?.payload;
  if (!point) {
    return null;
  }
  const shortMetric = metricPath.split('.').pop() ?? metricPath;
  const displayValue = useAggregatedValue ? point.aggregatedValue : point.value;
  const label = useAggregatedValue
    ? `${shortMetric} (${aggLabel.toLowerCase()})`
    : shortMetric;

  return (
    <div className="rounded border border-moon-200 dark:border-wb-border bg-wb-surface-raised px-8 py-4 text-xs">
      <div className="mb-2 text-moon-500 dark:text-wb-text-tertiary">
        {new Date(point.timestamp).toLocaleString()}
      </div>
      <div>
        <span className="font-semibold" style={{color: COLORS.metricLine}}>
          {formatDisplayValue(displayValue)}
        </span>{' '}
        <span className="text-moon-500 dark:text-wb-text-tertiary">
          {label}
        </span>
      </div>
    </div>
  );
};

const WeaveMetricChartInner: React.FC<WeaveMetricChartProps> = ({
  entityName,
  projectName,
  config,
}) => {
  const {
    chartData,
    loading,
    error,
    range,
    setRange,
    threshold,
    metricPath,
    hasWindow,
    aggFn,
    isStatus,
    timeDomain,
  } = useWeaveMetricChartData(entityName, projectName, config);

  const aggLabel = AGG_LABELS[aggFn] ?? aggFn;
  const useAggregatedValue = hasWindow || isStatus;
  const showRawValueLine = hasWindow && !isStatus;

  // Ensure the Y axis always includes the threshold so the reference line is visible
  const yDomain: [AxisDomainItem, AxisDomainItem] =
    threshold != null
      ? [0, (dataMax: number) => Math.max(dataMax, threshold * 1.1)]
      : [0, 'auto'];

  return (
    <div>
      <TimeRangeSelector range={range} setRange={setRange} />
      <div style={{height: CHART_HEIGHT}}>
        <ChartStatus
          loading={loading}
          error={error}
          hasData={chartData.length > 0}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{top: 4, right: 8, bottom: 0, left: -16}}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
              <XAxis
                dataKey="timestamp"
                type="number"
                domain={timeDomain}
                tickFormatter={(ts: number) => formatTime(ts, range)}
                tick={{fontSize: 10}}
                tickLine={false}
                axisLine={{stroke: COLORS.axis}}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{fontSize: 10}}
                tickLine={false}
                axisLine={{stroke: COLORS.axis}}
                width={40}
                domain={yDomain}
              />
              <Tooltip
                content={
                  <MetricTooltip
                    metricPath={metricPath}
                    useAggregatedValue={useAggregatedValue}
                    aggLabel={aggLabel}
                  />
                }
              />
              {showRawValueLine && (
                <Line
                  type="monotone"
                  dataKey="value"
                  name="value"
                  stroke={COLORS.rawValueLine}
                  strokeWidth={1}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              )}
              <Line
                type="monotone"
                dataKey={useAggregatedValue ? 'aggregatedValue' : 'value'}
                name={useAggregatedValue ? 'aggregatedValue' : 'value'}
                stroke={COLORS.metricLine}
                strokeWidth={2}
                dot={chartData.length <= 50}
                activeDot={{
                  r: 4,
                  stroke: COLORS.metricLine,
                  fill: THEME.bgSurface,
                }}
                isAnimationActive={false}
                connectNulls
              />
              {threshold != null && (
                <ReferenceLine
                  y={threshold}
                  stroke={COLORS.threshold}
                  strokeDasharray="6 3"
                  label={{
                    value: `Threshold: ${threshold}`,
                    position: 'insideTopLeft',
                    fontSize: 11,
                    fontWeight: 600,
                    fill: COLORS.threshold,
                  }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </ChartStatus>
      </div>
    </div>
  );
};

export const WeaveMetricChart: React.FC<WeaveMetricChartProps> = props => {
  return props.config.metricFilter.sourceType === 'SPANS' ? (
    <SpanMetricChart {...props} />
  ) : (
    <WeaveMetricChartInner {...props} />
  );
};
